import { StrictMode } from 'react'
import { createRoot } from 'react-dom/client'
import { QueryClientProvider } from '@tanstack/react-query'
import { ReactQueryDevtools } from '@tanstack/react-query-devtools'
import { queryClient } from './lib/queryClient'
import { initPerformanceMonitoring } from './lib/performanceMonitor'
import * as Sentry from '@sentry/react'
import './index.css'
import App from './App.tsx'

// ═══════════════════════════════════════════════════════════════
// 📊 SENTRY INITIALIZATION
// ═══════════════════════════════════════════════════════════════

// Only in production/preview (not in dev)
if (import.meta.env.PROD) {
  Sentry.init({
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || 'production',
    
    // Release tracking
    release: `coding-ui@${import.meta.env.VITE_APP_VERSION || '1.0.0'}`,
    
    // Integrations
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        maskAllText: false,
        blockAllMedia: false,
      }),
    ],
    
    // Performance monitoring
    tracesSampleRate: 0.1, // 10% of transactions
    
    // Session replay
    replaysSessionSampleRate: 0.1, // 10% of sessions
    replaysOnErrorSampleRate: 1.0, // 100% of sessions with errors
    
    // Ignore common errors
    ignoreErrors: [
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection',
      'Chrome extension',
      'webkit-masked-url',
    ],
    
    // Sanitize sensitive data
    beforeSend(event) {
      // Remove sensitive data
      if (event.request?.headers) {
        delete event.request.headers['Authorization'];
        delete event.request.headers['Cookie'];
      }
      return event;
    },
  });
  
  // Make Sentry available in console for testing
  (window as any).Sentry = Sentry;
  
  console.log('📊 Sentry error tracking initialized');
}

// ═══════════════════════════════════════════════════════════════
// ⚡ PERFORMANCE MONITORING
// ═══════════════════════════════════════════════════════════════

initPerformanceMonitoring();

// ═══════════════════════════════════════════════════════════════
// 🎨 DARK MODE INITIALIZATION
// ═══════════════════════════════════════════════════════════════

function initializeDarkMode() {
  try {
    const saved = localStorage.getItem("theme");
    const isDark = saved === "dark" || (!saved && window.matchMedia?.("(prefers-color-scheme: dark)").matches);
    if (isDark) {
      document.documentElement.classList.add("dark");
    } else {
      document.documentElement.classList.remove("dark");
    }
  } catch (e: unknown) {
    // Fallback if localStorage is not available
    if (window.matchMedia?.("(prefers-color-scheme: dark)").matches) {
      document.documentElement.classList.add("dark");
    }
  }
}

initializeDarkMode();

// ═══════════════════════════════════════════════════════════════
// ⚛️ REACT RENDER
// ═══════════════════════════════════════════════════════════════

createRoot(document.getElementById('root')!).render(
  <StrictMode>
    <QueryClientProvider client={queryClient}>
      <App />
      {/* DevTools only in development */}
      {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  </StrictMode>,
)